import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"
import LinksFieldset from "../../links/pages/LinksFieldset"
import type { LinkRow, LinksFieldsetLabels } from "../../links/pages/LinksFieldset"

// t018 `impl-forms` (FR-39): the seven `links.*` widget-UI keys join the
// item form's own label bundle -- `linksOptional` doesn't exist because
// the widget reuses this form's own `optional` (below) rather than a
// third copy of that string, the plan's own ruling.
interface AdminScheduleItemFormLabels {
  timeLabel: string
  titleLabel: string
  dayLabel: string
  descriptionLabel: string
  optional: string
  linksHeading: string
  linksRowHeading: string
  linksRemove: string
  linksAdd: string
  linksLabelLabel: string
  linksUrlLabel: string
  linksHint: string
  save: string
  delete: string
  deleteConfirm: string
}

interface DayOption {
  value: string
  label: string
}

interface AdminScheduleItemFormProps {
  heading: string
  subtitle: string
  backHref: string
  backLabel: string
  // The collection route for a new item (POST) or the item's own address
  // (PATCH) -- distinct from `backHref`, unlike the trip/day forms where
  // the resource's own address happened to serve both purposes.
  submitHref: string
  // `null` on the new-item form -- absent, not merely hidden, is what
  // keeps «Удалить пункт» off that screen (FR-19) and also tells this
  // component which HTTP verb `submitHref` expects.
  deleteHref: string | null
  item: {
    time: string | null
    // `null` on the new-item form (`ScheduleItem#title` has no default)
    // -- normalized to "" below, the same as every other nullable field.
    title: string | null
    description: string | null
    date: string
    // t018 `impl-forms` (FR-19, FR-20): the item's own links, in
    // `position` order -- `[]` on the new-item form.
    links: LinkRow[]
  }
  dayOptions: DayOption[]
  labels: AdminScheduleItemFormLabels
}

// The form's own local state stays flat, keyed by the model's bare
// attribute names plus `date` (a virtual field -- the day selector's own
// value, not a ScheduleItem column) -- matching every other admin form's
// reasoning (AdminTripForm.tsx): the server's failed-save `errors` prop
// round-trips under these same bare keys, and the wire payload nests them
// under "item" only at submit time (via `transform`). t018 `impl-forms`
// (FR-20): `links` joins this same flat state -- the widget itself owns
// none, so a failed save's re-render (the Inertia same-component
// redirect this form's own mounted state survives) shows every row
// exactly as typed, blanks included (FR-32).
interface ItemFormData {
  time: string
  title: string
  description: string
  date: string
  links: LinkRow[]
  authenticity_token: string
}

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const OPTIONAL_LABEL_CLASSES = "flex items-baseline gap-2 text-[13px] font-medium leading-[18px] text-gray-700"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// The item form (t009, `Schedule::AdminScheduleItemsController#new/#edit`):
// docs/design/admin.html's "State 3" verbatim for time/title/description
// and the delete control -- editing and creating share this one
// component, the heading and the delete button's presence being the only
// differences (both server-computed, FR-17). The day selector is new
// (A-4, FR-18): the design draws none, styled like every other input, the
// same "behaviour governs which fields exist, design governs how they
// look" reasoning the trip form's own time-zone field already set
// (plan Risk 2). t018 `impl-forms` (FR-30): the shared `links` widget
// mounts where the pre-t018 url/link-text pair stood -- after
// «Описание».
export default function AdminScheduleItemForm({
  heading, subtitle, backHref, backLabel, submitHref, deleteHref, item, dayOptions, labels,
}: AdminScheduleItemFormProps) {
  const form = useForm<ItemFormData>({
    time: item.time ?? "",
    title: item.title ?? "",
    description: item.description ?? "",
    date: item.date,
    links: item.links,
    authenticity_token: csrfToken(),
  })

  form.transform((data) => ({
    item: {
      time: data.time, title: data.title, description: data.description, date: data.date,
      links: data.links,
    },
    authenticity_token: data.authenticity_token,
  }))

  // t018 `impl-forms`: the widget's own labels, built once from this
  // form's flat bundle -- `optional` is this form's own key, reused
  // verbatim rather than a `links.*` copy (the plan's own ruling).
  const linksLabels: LinksFieldsetLabels = {
    heading: labels.linksHeading,
    optional: labels.optional,
    rowHeading: labels.linksRowHeading,
    remove: labels.linksRemove,
    add: labels.linksAdd,
    labelLabel: labels.linksLabelLabel,
    urlLabel: labels.linksUrlLabel,
    hint: labels.linksHint,
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (deleteHref === null) {
      form.post(submitHref)
    } else {
      form.patch(submitHref)
    }
  }

  function handleDelete(href: string) {
    if (window.confirm(labels.deleteConfirm)) {
      form.delete(href)
    }
  }

  return (
    <AdminLayout>
      <Link href={backHref} className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {backLabel}
      </Link>
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading}</h2>
        <p className="text-[13px] leading-[18px] text-gray-500">{subtitle}</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.timeLabel}</span>
          <input
            type="time"
            value={form.data.time}
            onChange={(event) => form.setData("time", event.target.value)}
            className={`${INPUT_CLASSES} w-26 tabular-nums`}
          />
          {form.errors.time && <span className={ERROR_CLASSES}>{form.errors.time}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.titleLabel}</span>
          <input
            type="text"
            value={form.data.title}
            onChange={(event) => form.setData("title", event.target.value)}
            className={INPUT_CLASSES}
          />
          {form.errors.title && <span className={ERROR_CLASSES}>{form.errors.title}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.dayLabel}</span>
          <select
            value={form.data.date}
            onChange={(event) => form.setData("date", event.target.value)}
            className={INPUT_CLASSES}
          >
            {dayOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.descriptionLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <textarea
            value={form.data.description}
            onChange={(event) => form.setData("description", event.target.value)}
            className="min-h-20 rounded-[10px] border border-gray-300 px-3 py-2.5 text-[15px] leading-[22px] text-gray-900"
          />
        </label>
        <LinksFieldset
          rows={form.data.links}
          onChange={(links) => form.setData("links", links)}
          labels={linksLabels}
          errors={form.errors as Record<string, string | undefined>}
        />
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
        {deleteHref !== null && (
          <button
            type="button"
            onClick={() => handleDelete(deleteHref)}
            className="flex items-center justify-center gap-1.5 py-1 text-sm font-medium text-red-700"
          >
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9a1.5 1.5 0 001.5 1.4h3.6A1.5 1.5 0 0011.3 13l.7-9M6.7 7v4.5M9.3 7v4.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {labels.delete}
          </button>
        )}
      </form>
    </AdminLayout>
  )
}
